"use client"

import {
    LogOut,
    User,
    CreditCard,
    Gift,
} from "lucide-react"
import { useState } from "react"
import { MobileNav } from "./mobile-nav"
import { SettingsModal } from "@/components/modal-dialogs/settings-modal/settings-modal"
import { WorkspaceCreditsBadge } from "@/components/workspace/workspace-credits-badge"
import { ProductHuntCard } from "./product-hunt-card"
import { useRouter } from "next/navigation"
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
    DropdownMenuLabel,
    DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu"
import { useAppDispatch } from "@/app/store/hooks"
import { logout } from "@/app/store/slices/authSlice"
import { useLogoutMutation } from "@/app/store/api/authApi"
import { useGetProfileQuery } from "@/app/store/api/userApi"
import { useAppSelector } from "@/app/store/hooks"
import { trackEvent, resetMixpanel } from "@/lib/mixpanel"

import { Avatar } from "@/components/ui/avatar"
import { getInitials } from "@/lib/utils"
import { LoginPopover } from "@/components/auth/login-popover"

export function DashboardHeader() {
    const router = useRouter();
    const dispatch = useAppDispatch();
    const [logoutApi] = useLogoutMutation();
    const { user, isAuthenticated } = useAppSelector((state) => state.auth);
    const [isLoginPopoverOpen, setIsLoginPopoverOpen] = useState(false);

    // Fetch fresh profile data to keep credits badge up-to-date via store
    useGetProfileQuery(undefined, {
        refetchOnMountOrArgChange: true,
        skip: !isAuthenticated
    });

    const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false)
    const [settingsInitialTab, setSettingsInitialTab] = useState<"basic" | "referral" | "billing">("basic")

    const initials = getInitials(user?.firstName, user?.lastName);

    const handleLogout = async () => {
        trackEvent("User Logged Out");
        try {
            await logoutApi({}).unwrap();
        } catch (error) {
            console.error("Logout API failed", error);
        }
        resetMixpanel();
        dispatch(logout());
    };

    const openSettings = (tab: "basic" | "referral" | "billing") => {
        trackEvent("Settings Opened", { tab });
        setSettingsInitialTab(tab)
        setIsSettingsModalOpen(true)
    }

    return (
        <>
            <header className="flex items-center justify-between px-4 md:px-6 gap-3 bg-white md:bg-transparent md:backdrop-blur flex-none pt-4">
                <div className="flex items-center gap-4">
                    {isAuthenticated && <MobileNav />}
                    <ProductHuntCard />
                </div>
                <div className="flex items-center gap-3">
                    {/* Refer and Earn Button */}
                    <button
                        onClick={() => { 
                            if (!isAuthenticated) {
                                setIsLoginPopoverOpen(true);
                                return;
                            }
                            trackEvent("Refer & Earn Clicked"); 
                            openSettings("referral"); 
                        }}
                        className="flex items-center gap-2 text-xs font-semibold text-blue-600 bg-blue-50/50 border border-blue-200/60 rounded-full px-3 py-1.5 cursor-pointer hover:bg-blue-50 transition-colors"
                    >
                        <Gift className="h-3.5 w-3.5" />
                        Refer & Earn
                    </button>


                    <WorkspaceCreditsBadge
                        onClick={() => { trackEvent("Upgrade Button Clicked", { source: "dashboard_header" }); router.push('/pricing'); }}
                    />

                    {isAuthenticated ? (
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <div>
                                    <Avatar
                                        className="h-8 w-8 ring-2 ring-white shadow-md cursor-pointer hover:opacity-90 transition-all hover:scale-105"
                                        src={user?.imgUrl}
                                        fallback={initials}
                                        fallbackClassName="bg-orange-500 text-white font-bold text-xs"
                                    />
                                </div>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-56">
                                <DropdownMenuLabel className="font-normal">
                                    <div className="flex flex-col space-y-1">
                                        <p className="text-sm font-medium leading-none">{user?.firstName} {user?.lastName}</p>
                                        <p className="text-xs leading-none text-muted-foreground">
                                            {user?.email}
                                        </p>
                                    </div>
                                </DropdownMenuLabel>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={() => openSettings("basic")} className="cursor-pointer">
                                    <User className="mr-2 h-4 w-4" />
                                    <span>Profile</span>
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => openSettings("billing")} className="cursor-pointer">
                                    <CreditCard className="mr-2 h-4 w-4" />
                                    <span>Billing</span>
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={handleLogout} className="text-red-600 focus:text-red-600 cursor-pointer">
                                    <LogOut className="mr-2 h-4 w-4" />
                                    <span>Logout</span>
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    ) : (
                        <button
                            onClick={() => setIsLoginPopoverOpen(true)}
                            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg text-xs font-bold transition-all shadow-lg shadow-blue-600/20 active:scale-[0.98] cursor-pointer"
                        >
                            Sign In
                        </button>
                    )}
                </div>
            </header>
            <SettingsModal
                isOpen={isSettingsModalOpen}
                onClose={() => setIsSettingsModalOpen(false)}
                initialTab={settingsInitialTab}
            />
            <LoginPopover
                isOpen={isLoginPopoverOpen}
                onClose={() => setIsLoginPopoverOpen(false)}
            />
        </>
    )
}
